import { demoRepository } from '../repository/demoRepository';
import { calculations } from './calculations';

const normaliseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
const percent = (numerator, denominator) => denominator > 0 ? calculations.roundPercent(numerator / denominator) : 0;

/**
 * Default portfolio scope represents currently held properties, so sold and
 * archived records are historical and excluded. Explicit property selection is
 * respected so historical reports can still be generated for a sold/archived item.
 */
const getScopedProperties = (propertyIds = null) => {
  const requestedIds = Array.isArray(propertyIds) ? propertyIds.map(String) : null;
  return demoRepository.getAllProperties().filter(property => {
    const propertyId = String(property?.id || '');
    if (requestedIds !== null) return requestedIds.includes(propertyId);
    const status = normaliseStatus(property?.status);
    return status !== 'archived' && status !== 'sold';
  });
};

const sum = (items, selector) => calculations.roundMoney(items.reduce((total, item) => total + (Number(selector(item)) || 0), 0));

export const calculateAuthoritativePortfolioAnnualRent = (propertyIds = null) => {
  const properties = getScopedProperties(propertyIds);
  return sum(properties, property => calculations.getPropertyAnnualRentPosition(property.id).annualRent);
};


/**
 * Canonical property-use classification for portfolio projections.
 * This is deliberately shared with Dashboard consumers so PPR and investment
 * properties cannot drift into different calculation scopes.
 */
export const isOwnerOccupiedProperty = (property) => {
  const financial = property?.metadata?.financial || {};
  const loanType = normaliseStatus(property?.loanType || financial.loanType || '');
  const explicitUse = normaliseStatus(property?.propertyUse || property?.useType || property?.occupancyType || '');
  return loanType.includes('owner_occupier')
    || explicitUse.includes('owner_occupied')
    || explicitUse.includes('primary_residence')
    || explicitUse === 'ppr';
};

/**
 * Current projected portfolio position.
 *
 * Investment position = total anticipated monthly net rent
 *                     - total current investment monthly instalments.
 *
 * PPR instalments are intentionally kept separate and are never deducted from
 * the investment-position metric. If any included investment property's rent
 * or instalment inputs are incomplete, the aggregate investment position is
 * reported as incomplete instead of displaying a misleading partial result.
 */
export const getProjectedPortfolioPosition = (propertyIds = null, asOf = Date.now()) => {
  const properties = getScopedProperties(propertyIds);
  const incompleteInvestmentPropertyIds = new Set();
  const result = {
    propertyCount: properties.length,
    investmentPropertyCount: 0,
    pprPropertyCount: 0,
    anticipatedMonthlyGrossRent: 0,
    anticipatedMonthlyNetRent: 0,
    investmentMonthlyInstallment: 0,
    pprMonthlyInstallment: 0,
    incompleteInvestmentRentProperties: 0,
    incompleteInvestmentLoanProperties: 0,
    incompletePprLoanProperties: 0,
    incompleteInvestmentLoanCount: 0,
    incompletePprLoanCount: 0,
    positiveProperties: 0,
    negativeProperties: 0,
    neutralProperties: 0
  };

  properties.forEach(property => {
    const projection = calculations.getPropertyIndicativeCashFlowPosition(property.id, asOf);
    const ownerOccupied = isOwnerOccupiedProperty(property);

    if (ownerOccupied) {
      result.pprPropertyCount += 1;
      if (projection?.loan?.installmentComplete) {
        result.pprMonthlyInstallment += Number(projection.loan.monthlyInstallment || 0);
      } else {
        result.incompletePprLoanProperties += 1;
        result.incompletePprLoanCount += Number(projection?.loan?.incompleteLoanCount || 1);
      }
      return;
    }

    result.investmentPropertyCount += 1;

    if (projection?.rent?.rentComplete) {
      result.anticipatedMonthlyGrossRent += Number(projection.rent.monthlyGrossRent || 0);
      result.anticipatedMonthlyNetRent += Number(projection.rent.monthlyNetRent || 0);
    } else {
      result.incompleteInvestmentRentProperties += 1;
      incompleteInvestmentPropertyIds.add(String(property.id));
    }

    if (projection?.loan?.installmentComplete) {
      result.investmentMonthlyInstallment += Number(projection.loan.monthlyInstallment || 0);
    } else {
      result.incompleteInvestmentLoanProperties += 1;
      result.incompleteInvestmentLoanCount += Number(projection?.loan?.incompleteLoanCount || 1);
      incompleteInvestmentPropertyIds.add(String(property.id));
    }

    if (projection?.complete) {
      if (projection.classification === 'positive') result.positiveProperties += 1;
      else if (projection.classification === 'negative') result.negativeProperties += 1;
      else result.neutralProperties += 1;
    }
  });

  result.anticipatedMonthlyGrossRent = calculations.roundMoney(result.anticipatedMonthlyGrossRent);
  result.anticipatedMonthlyNetRent = calculations.roundMoney(result.anticipatedMonthlyNetRent);
  result.investmentMonthlyInstallment = calculations.roundMoney(result.investmentMonthlyInstallment);
  result.pprMonthlyInstallment = calculations.roundMoney(result.pprMonthlyInstallment);

  result.anticipatedAnnualGrossRent = calculations.roundMoney(result.anticipatedMonthlyGrossRent * 12);
  result.anticipatedAnnualNetRent = calculations.roundMoney(result.anticipatedMonthlyNetRent * 12);
  result.investmentAnnualInstallment = calculations.roundMoney(result.investmentMonthlyInstallment * 12);
  result.pprAnnualInstallment = calculations.roundMoney(result.pprMonthlyInstallment * 12);

  result.investmentProjectionComplete = (
    result.incompleteInvestmentRentProperties === 0 &&
    result.incompleteInvestmentLoanProperties === 0
  );
  result.pprProjectionComplete = result.incompletePprLoanProperties === 0;

  result.projectedInvestmentMonthlyPosition = result.investmentProjectionComplete
    ? calculations.roundMoney(result.anticipatedMonthlyNetRent - result.investmentMonthlyInstallment)
    : null;
  result.projectedInvestmentAnnualPosition = result.projectedInvestmentMonthlyPosition === null
    ? null
    : calculations.roundMoney(result.projectedInvestmentMonthlyPosition * 12);

  result.totalMonthlyInstallment = calculations.roundMoney(
    result.pprMonthlyInstallment + result.investmentMonthlyInstallment
  );
  result.totalAnnualInstallment = calculations.roundMoney(result.totalMonthlyInstallment * 12);

  result.incompleteInvestmentProperties = incompleteInvestmentPropertyIds.size;
  result.incompletePprLoans = result.incompletePprLoanCount;

  result.indicativeMonthlyCashFlow = result.projectedInvestmentMonthlyPosition;
  result.indicativeAnnualCashFlow = result.projectedInvestmentAnnualPosition;
  result.source = 'current_property_projection_engine';
  result.asOf = asOf;

  return result;
};

const getTrailingTwelveMonthFinanceAverage = (propertyIds, asOf = new Date()) => {
  if (typeof calculations.getLoanPaymentsByProperty !== 'function' || typeof calculations.getLoanPaymentCashOutflow !== 'function') {
    return { totalCashOutflow: 0, averageMonthlyCashOutflow: 0, paymentCount: 0, undatedPaymentCount: 0 };
  }

  const end = asOf instanceof Date ? new Date(asOf.getTime()) : new Date(asOf);
  const safeEnd = Number.isNaN(end.getTime()) ? new Date() : end;
  const start = new Date(safeEnd.getTime());
  start.setFullYear(start.getFullYear() - 1);
  const uniquePayments = new Map();

  propertyIds.forEach(propertyId => {
    calculations.getLoanPaymentsByProperty(propertyId).forEach(payment => {
      const key = payment?.id || `${propertyId}:${payment?.paymentDate || ''}:${payment?.amountDeducted || payment?.totalRepayment || ''}`;
      if (!uniquePayments.has(key)) uniquePayments.set(key, payment);
    });
  });

  let totalCashOutflow = 0;
  let paymentCount = 0;
  let undatedPaymentCount = 0;
  uniquePayments.forEach(payment => {
    const rawDate = payment?.paymentDate ?? payment?.date;
    if (!rawDate) {
      undatedPaymentCount += 1;
      return;
    }
    const paymentDate = new Date(rawDate);
    if (Number.isNaN(paymentDate.getTime())) {
      undatedPaymentCount += 1;
      return;
    }
    if (paymentDate < start || paymentDate > safeEnd) return;
    totalCashOutflow += calculations.getLoanPaymentCashOutflow(payment);
    paymentCount += 1;
  });

  totalCashOutflow = calculations.roundMoney(totalCashOutflow);
  return {
    totalCashOutflow,
    averageMonthlyCashOutflow: calculations.roundMoney(totalCashOutflow / 12),
    paymentCount,
    undatedPaymentCount,
    periodStart: start.getTime(),
    periodEnd: safeEnd.getTime()
  };
};

export const getAuthoritativePortfolioSummary = (
  propertyIds = null,
  financialYear = calculations.getAustralianFinancialYear(new Date())
) => {
  const currentFinancialYear = financialYear && financialYear !== 'All'
    ? financialYear
    : calculations.getAustralianFinancialYear(new Date());
  const properties = getScopedProperties(propertyIds);
  const scopedIds = properties.map(property => property.id);
  const propertySummaries = properties
    .map(property => ({ property, summary: calculations.getPropertySummary(property.id, currentFinancialYear) }))
    .filter(item => item.summary);
  const summaries = propertySummaries.map(item => item.summary);

  const totalMarketValue = sum(propertySummaries, ({ property }) => property?.currentMarketValue);
  const totalAcquisitionCost = sum(summaries, summary => summary.totalAcquisitionCost);
  const totalLoanBalance = sum(summaries, summary => summary.totalLoanBalance);
  const sumPropertyEquity = sum(summaries, summary => summary.equity);
  const totalEquity = calculations.calculateEquity(totalMarketValue, totalLoanBalance);
  const equityReconciliationDifference = calculations.roundMoney(totalEquity - sumPropertyEquity);
  const portfolioLVR = calculations.calculateLVR(totalLoanBalance, totalMarketValue);

  const totalSpaces = summaries.reduce((total, summary) => total + (Number(summary.spacesCount) || 0), 0);
  const occupiedSpaces = summaries.reduce((total, summary) => total + (Number(summary.occupiedSpaces) || 0), 0);
  const occupancyRate = totalSpaces > 0 ? calculations.roundPercent(occupiedSpaces / totalSpaces) : 0;
  const vacancyRate = totalSpaces > 0 ? calculations.roundPercent((totalSpaces - occupiedSpaces) / totalSpaces) : 0;

  const annualRent = sum(summaries, summary => summary.annualRent);
  const grossYield = calculations.calculateGrossYield(annualRent, totalMarketValue);
  const annualRentUnsupportedCount = summaries.reduce((total, summary) => total + (Number(summary.annualRentUnsupportedCount) || 0), 0);
  const annualRentComplete = summaries.every(summary => summary.annualRentComplete !== false);

  const actualOperatingIncome = sum(summaries, summary => summary.actualOperatingIncome);
  const actualOperatingExpenses = sum(summaries, summary => summary.actualOperatingExpenses);
  const netOperatingIncome = calculations.roundMoney(actualOperatingIncome - actualOperatingExpenses);
  const performanceReviewRequired = sum(summaries, summary => summary.performanceReviewRequired);
  const performanceReviewRequiredCount = summaries.reduce((total, summary) => total + (Number(summary.performanceReviewRequiredCount) || 0), 0);

  const financingCashOutflow = sum(summaries, summary => summary.financingCashOutflow);
  const loanInterestPaid = sum(summaries, summary => summary.loanInterestPaid);
  const loanPrincipalPaid = sum(summaries, summary => summary.loanPrincipalPaid);
  const loanFeesPaid = sum(summaries, summary => summary.loanFeesPaid);
  const cashFlowAfterFinance = calculations.roundMoney(netOperatingIncome - financingCashOutflow);
  const sumPropertyCashFlowAfterFinance = sum(summaries, summary => summary.cashFlowAfterFinance ?? summary.netCashFlow);
  const cashFlowReconciliationDifference = calculations.roundMoney(cashFlowAfterFinance - sumPropertyCashFlowAfterFinance);

  const trailing12MonthOperatingIncome = sum(summaries, summary => summary.trailing12MonthOperatingIncome);
  const trailing12MonthOperatingExpenses = sum(summaries, summary => summary.trailing12MonthOperatingExpenses);
  const trailing12MonthNOI = calculations.roundMoney(trailing12MonthOperatingIncome - trailing12MonthOperatingExpenses);
  const trailing12MonthReviewRequired = sum(summaries, summary => summary.trailing12MonthReviewRequired);
  const trailing12MonthReviewRequiredCount = summaries.reduce((total, summary) => total + (Number(summary.trailing12MonthReviewRequiredCount) || 0), 0);
  const netYield = totalMarketValue > 0 ? calculations.roundPercent(trailing12MonthNOI / totalMarketValue) : 0;
  const netYieldComplete = totalMarketValue > 0 && summaries.length > 0 && summaries.every(summary => summary.netYieldComplete);

  const activePolicyPremiumCommitment = scopedIds.length > 0
    ? calculations.calculateInsuranceExpenses({ propertyIds: scopedIds })
    : 0;
  const trailingFinance = getTrailingTwelveMonthFinanceAverage(scopedIds);
  const financeReviewPropertyCount = summaries.filter(summary => !summary.financeCashFlowComplete || (Number(summary.financeReviewCount) || 0) > 0).length;
  const dataQualityReviewPropertyCount = summaries.filter(summary => summary.dataQualityStatus !== 'complete').length;
  const ownershipReviewPropertyCount = summaries.filter(summary => summary.ownershipBasis && summary.ownershipBasis !== 'full_property').length;
  const openItemsCount = summaries.reduce((total, summary) => total + (Number(summary.openItemsCount) || 0), 0);

  const formulaChecks = {
    equity: totalEquity === calculations.roundMoney(totalMarketValue - totalLoanBalance),
    equityReconcilesToProperties: Math.abs(equityReconciliationDifference) <= 0.02,
    lvr: totalMarketValue === 0 || Math.abs(portfolioLVR - percent(totalLoanBalance, totalMarketValue)) < 0.0001,
    grossYield: totalMarketValue === 0 || Math.abs(grossYield - percent(annualRent, totalMarketValue)) < 0.0001,
    noi: netOperatingIncome === calculations.roundMoney(actualOperatingIncome - actualOperatingExpenses),
    cashFlowAfterFinance: cashFlowAfterFinance === calculations.roundMoney(netOperatingIncome - financingCashOutflow),
    cashFlowReconcilesToProperties: Math.abs(cashFlowReconciliationDifference) <= 0.02,
    netYield: totalMarketValue === 0 || Math.abs(netYield - percent(trailing12MonthNOI, totalMarketValue)) < 0.0001
  };

  const dataQualityIssues = [];
  if (properties.length === 0) dataQualityIssues.push('The selected portfolio scope contains no properties.');
  const missingMarketValueCount = propertySummaries.filter(({ property }) => !(Number(property?.currentMarketValue) > 0)).length;
  if (missingMarketValueCount > 0) dataQualityIssues.push(`${missingMarketValueCount} included property record(s) have a missing/zero current market value.`);
  if (!annualRentComplete) dataQualityIssues.push(`${annualRentUnsupportedCount} rent record(s) use an unsupported/custom rent basis and are excluded from portfolio expected rent.`);
  if (performanceReviewRequiredCount > 0) dataQualityIssues.push(`${performanceReviewRequiredCount} Paid transaction(s) across the selected properties require more specific classification before FY ${currentFinancialYear} portfolio NOI is fully complete.`);
  if (financeReviewPropertyCount > 0) dataQualityIssues.push(`${financeReviewPropertyCount} included propert${financeReviewPropertyCount === 1 ? 'y has' : 'ies have'} provisional or reconciliation-review finance cash flow for FY ${currentFinancialYear}.`);
  if (!netYieldComplete && properties.length > 0) dataQualityIssues.push('Portfolio trailing-12-month net yield is provisional because one or more included properties do not yet have complete classified 12-month operating coverage.');
  if (ownershipReviewPropertyCount > 0) dataQualityIssues.push(`${ownershipReviewPropertyCount} included propert${ownershipReviewPropertyCount === 1 ? 'y is' : 'ies are'} shown on a full-property basis despite a recorded ownership percentage below 100%.`);
  if (trailingFinance.undatedPaymentCount > 0) dataQualityIssues.push(`${trailingFinance.undatedPaymentCount} loan-payment record(s) lack a usable payment date and are excluded from the trailing-12-month monthly-finance average.`);
  if (!Object.values(formulaChecks).every(Boolean)) dataQualityIssues.push('One or more portfolio arithmetic reconciliation checks failed.');

  const calculationHelp = {
    marketValue: `${calculations.formatCurrencyAUD(totalMarketValue)} portfolio market value = the sum of current market values for ${properties.length} included propert${properties.length === 1 ? 'y' : 'ies'}.`,
    acquisitionCost: `${calculations.formatCurrencyAUD(totalAcquisitionCost)} total acquisition cost = the sum of each included property's authoritative acquisition cost.`,
    loanBalance: `${calculations.formatCurrencyAUD(totalLoanBalance)} portfolio debt = the sum of each included property's authoritative current loan balance. Pending/closed/refinanced/archived loans are not treated as current debt, and property-level fallback balances are not added on top of active linked loans.`,
    equity: `${calculations.formatCurrencyAUD(totalMarketValue)} market value − ${calculations.formatCurrencyAUD(totalLoanBalance)} debt = ${calculations.formatCurrencyAUD(totalEquity)} portfolio equity. This also reconciles to ${calculations.formatCurrencyAUD(sumPropertyEquity)} summed property equity.`,
    lvr: totalMarketValue > 0
      ? `${calculations.formatCurrencyAUD(totalLoanBalance)} total debt ÷ ${calculations.formatCurrencyAUD(totalMarketValue)} total market value × 100 = ${calculations.formatPercentage(portfolioLVR)} portfolio LVR. Property LVR percentages are not averaged.`
      : 'Portfolio LVR cannot be meaningfully calculated until the selected properties have a non-zero total current market value.',
    annualRent: `${calculations.formatCurrencyAUD(annualRent)} expected annual gross rent = the sum of each included property's basis-normalised expected rent. Lease and occupied-space fallbacks are resolved at property level before aggregation.`,
    grossYield: totalMarketValue > 0
      ? `${calculations.formatCurrencyAUD(annualRent)} expected annual gross rent ÷ ${calculations.formatCurrencyAUD(totalMarketValue)} portfolio market value = ${calculations.formatPercentage(grossYield)} gross yield. Property yields are not averaged.`
      : 'Portfolio gross yield cannot be meaningfully calculated until total current market value is above zero.',
    occupancy: totalSpaces > 0
      ? `${occupiedSpaces} occupied rentable spaces ÷ ${totalSpaces} total rentable spaces = ${calculations.formatPercentage(occupancyRate)} portfolio occupancy. Property occupancy percentages are not averaged.`
      : 'Portfolio occupancy is 0% because the selected properties contain no active rentable spaces.',
    operatingIncome: `${calculations.formatCurrencyAUD(actualOperatingIncome)} actual operating income = the sum of Paid operating-income transactions for the selected properties in FY ${currentFinancialYear}.`,
    operatingExpenses: `${calculations.formatCurrencyAUD(actualOperatingExpenses)} actual operating expenses = the sum of Paid operating-cost transactions for the selected properties in FY ${currentFinancialYear}. Active insurance-policy premiums are shown separately as a commitment and are not added again to NOI.`,
    netOperatingIncome: `${calculations.formatCurrencyAUD(actualOperatingIncome)} operating income − ${calculations.formatCurrencyAUD(actualOperatingExpenses)} operating expenses = ${calculations.formatCurrencyAUD(netOperatingIncome)} portfolio NOI for FY ${currentFinancialYear}.`,
    financingCashOutflow: `${calculations.formatCurrencyAUD(financingCashOutflow)} portfolio financing cash outflow = the sum of each included property's actual/provisional financing cash outflow for FY ${currentFinancialYear}. Interest, principal and fees are repayment components and are not deducted twice.`,
    cashFlowAfterFinance: `${calculations.formatCurrencyAUD(netOperatingIncome)} NOI − ${calculations.formatCurrencyAUD(financingCashOutflow)} financing cash outflow = ${calculations.formatCurrencyAUD(cashFlowAfterFinance)} portfolio cash flow after finance for FY ${currentFinancialYear}.`,
    netYield: totalMarketValue > 0
      ? `${calculations.formatCurrencyAUD(trailing12MonthNOI)} trailing-12-month portfolio NOI ÷ ${calculations.formatCurrencyAUD(totalMarketValue)} portfolio market value = ${calculations.formatPercentage(netYield)} portfolio net yield. Property net-yield percentages are not averaged.${netYieldComplete ? '' : ' The result is provisional because one or more included property datasets lack complete classified 12-month coverage.'}`
      : 'Portfolio net yield cannot be meaningfully calculated until total current market value is above zero.',
    insurance: `${calculations.formatCurrencyAUD(activePolicyPremiumCommitment)} is the current active-policy premium commitment for the selected properties. It is not added again to actual operating expenses, avoiding insurance double-counting.`,
    monthlyFinanceAverage: `${calculations.formatCurrencyAUD(trailingFinance.totalCashOutflow)} actual loan-payment cash outflow over the trailing 12 months ÷ 12 = ${calculations.formatCurrencyAUD(trailingFinance.averageMonthlyCashOutflow)} average monthly financing cash outflow.`
  };

  return {
    totalProperties: properties.length,
    includedPropertyIds: scopedIds,
    portfolioScope: Array.isArray(propertyIds) ? 'selected_properties' : 'current_portfolio',
    currentFinancialYear,
    totalSpaces,
    occupiedSpaces,
    vacancyRate,
    occupancyRate,
    totalMarketValue,
    totalAcquisitionCost,
    unrealisedGain: calculations.roundMoney(totalMarketValue - totalAcquisitionCost),
    totalLoanBalance,
    sumPropertyEquity,
    totalEquity,
    equityReconciliationDifference,
    portfolioLVR,
    annualRent,
    annualRentUnsupportedCount,
    annualRentComplete,
    actualOperatingIncome,
    actualOperatingExpenses,
    netOperatingIncome,
    performanceReviewRequired,
    performanceReviewRequiredCount,
    activePolicyPremiumCommitment,
    annualInsurance: activePolicyPremiumCommitment,
    annualInsuranceIncludedInNOI: false,
    annualExpenses: actualOperatingExpenses,
    grossYield,
    financingCashOutflow,
    loanInterestPaid,
    loanPrincipalPaid,
    loanFeesPaid,
    cashFlowAfterFinance,
    sumPropertyCashFlowAfterFinance,
    cashFlowReconciliationDifference,
    cashFlowAfterLoanPayments: cashFlowAfterFinance,
    trailing12MonthOperatingIncome,
    trailing12MonthOperatingExpenses,
    trailing12MonthNOI,
    trailing12MonthReviewRequired,
    trailing12MonthReviewRequiredCount,
    netYield,
    netYieldBasis: 'portfolio_trailing_12_month_noi_over_total_current_market_value',
    netYieldComplete,
    trailing12MonthFinancingCashOutflow: trailingFinance.totalCashOutflow,
    averageMonthlyFinancingCashOutflow: trailingFinance.averageMonthlyCashOutflow,
    totalMonthlyLoanPayments: trailingFinance.averageMonthlyCashOutflow,
    monthlyLoanPaymentBasis: 'trailing_12_month_actual_finance_cash_outflow_average',
    financeReviewPropertyCount,
    dataQualityReviewPropertyCount,
    ownershipReviewPropertyCount,
    openItemsCount,
    formulaChecks,
    formulaChecksPassed: Object.values(formulaChecks).every(Boolean),
    dataQualityIssues,
    dataQualityStatus: properties.length === 0 ? 'empty_scope' : (dataQualityIssues.length === 0 ? 'complete' : 'review_required'),
    calculationHelp
  };
};

calculations.calculateAuthoritativePortfolioAnnualRent = calculateAuthoritativePortfolioAnnualRent;
calculations.calculatePortfolioAnnualRent = calculateAuthoritativePortfolioAnnualRent;
calculations.isOwnerOccupiedProperty = isOwnerOccupiedProperty;
calculations.getProjectedPortfolioPosition = getProjectedPortfolioPosition;
calculations.getAuthoritativePortfolioSummary = getAuthoritativePortfolioSummary;
calculations.getPortfolioSummary = getAuthoritativePortfolioSummary;